import { useMemo } from 'react';
import { useGPS } from '../contexts/GPSContext.jsx';
import { safeParseDate } from '../utils/dateUtils.js';
import { isDraftIssue, isAbandonedIssue, isInterventionDoneIssue, uniqueInterventionsByStoreDay } from '../utils/gpsStatusCategories.js';

/**
 * @param {{ excludeDrafts?: boolean }} options
 *   excludeDrafts: drop "Brouillon" (draft) tickets — used by the Général tab,
 *   which excludes drafts entirely (they stay visible in Secteurs / Tickets).
 */
export const useGPSStats = ({ excludeDrafts = false } = {}) => {
  const gps = useGPS();

  const stats = useMemo(() => {
    const issues = excludeDrafts ? gps.issues.filter(i => !isDraftIssue(i)) : gps.issues;
    const previousIssues = excludeDrafts ? gps.previousIssues.filter(i => !isDraftIssue(i)) : gps.previousIssues;

    const compliantRequests = issues.filter(issue => issue.request_compliant && !issue.is_child);
    const noCompliantRequests = issues.filter(issue => !issue.request_compliant && !issue.is_child);
    const compliantInterventions = issues.filter(issue => issue.intervention_compliant && !issue.is_child);
    const noCompliantInterventions = issues.filter(issue => !issue.intervention_compliant && !issue.is_child);
    const prevCompliantRequests = previousIssues.filter(issue => issue.request_compliant);
    const prevNoCompliantRequests = previousIssues.filter(issue => !issue.request_compliant && !issue.is_child);
    const prevCompliantInterventions = previousIssues.filter(issue => issue.intervention_compliant && !issue.is_child);
    const prevNoCompliantInterventions = previousIssues.filter(issue => !issue.intervention_compliant && !issue.is_child);
    const totalRequests = compliantRequests.length + noCompliantRequests.length;
    const totalInterventions = compliantInterventions.length + noCompliantInterventions.length;
    const prevTotalRequests = prevCompliantRequests.length + prevNoCompliantRequests.length;
    const prevTotalInterventions = prevCompliantInterventions.length + prevNoCompliantInterventions.length;

    // "Reçus" subset (created within the period) — also the base for "Magasins
    // concernés" so that count reflects stores with a ticket RECEIVED in the period.
    const openTickets = issues.filter(issue => {
      const d = safeParseDate(issue.created);
      return d && d >= gps.startDate && d <= gps.endDate;
    });
    const prevOpenTickets = previousIssues.filter(issue => {
      const d = safeParseDate(issue.created);
      return d && d >= gps.previousStartDate && d <= gps.previousEndDate;
    });

    // Map a raw duedate to a YYYY-MM-DD key for the per-store/day dedup.
    const dayKey = (v) => {
      const d = safeParseDate(v);
      return d ? d.toISOString().slice(0, 10) : String(v || '').slice(0, 10);
    };

    return {
      openTickets,
      ongoingTickets: issues.filter(({ status_category }) => status_category !== 'DONE'),
      distinctsInterventionPoints: [...new Set(openTickets.map(issue => issue.intervention_point))],
      abandonedTickets: issues.filter(isAbandonedIssue),
      closedTickets: issues.filter(issue => issue.status_category === 'DONE'),
      standByTickets: issues.filter(issue => issue.status === 'STANDBY'),
      interventionsDone: uniqueInterventionsByStoreDay(issues.filter(isInterventionDoneIssue), dayKey),
      compliantRequests,
      noCompliantRequests,
      compliantInterventions,
      noCompliantInterventions,
      compliantRequestsPercentage: totalRequests > 0 ? Math.round((compliantRequests.length / totalRequests) * 100) : 0,
      compliantInterventionsPercentage: totalInterventions > 0 ? Math.round((compliantInterventions.length / totalInterventions) * 100) : 0,
      noCompliantRequestsPercentage: totalRequests > 0 ? Math.round((noCompliantRequests.length / totalRequests) * 100) : 0,
      noCompliantInterventionsPercentage: totalInterventions > 0 ? Math.round((noCompliantInterventions.length / totalInterventions) * 100) : 0,
      previous: {
        openTickets: prevOpenTickets,
        ongoingTickets: previousIssues.filter(({ status_category }) => status_category !== 'DONE'),
        distinctsInterventionPoints: [...new Set(prevOpenTickets.map(issue => issue.intervention_point))],
        abandonedTickets: previousIssues.filter(isAbandonedIssue),
        closedTickets: previousIssues.filter(issue => issue.status_category === 'DONE'),
        standByTickets: previousIssues.filter(issue => issue.status === 'STANDBY'),
        interventionsDone: uniqueInterventionsByStoreDay(previousIssues.filter(isInterventionDoneIssue), dayKey),
        compliantRequests: prevCompliantRequests,
        noCompliantRequests: prevNoCompliantRequests,
        compliantInterventions: prevCompliantInterventions,
        noCompliantInterventions: prevNoCompliantInterventions,
        compliantRequestsPercentage: prevTotalRequests > 0 ? Math.round((prevCompliantRequests.length / prevTotalRequests) * 100) : 0,
        compliantInterventionsPercentage: prevTotalInterventions > 0 ? Math.round((prevCompliantInterventions.length / prevTotalInterventions) * 100) : 0,
        noCompliantRequestsPercentage: prevTotalRequests > 0 ? Math.round((prevNoCompliantRequests.length / prevTotalRequests) * 100) : 0,
        noCompliantInterventionsPercentage: prevTotalInterventions > 0 ? Math.round((prevNoCompliantInterventions.length / prevTotalInterventions) * 100) : 0,
      }
    };
  }, [gps.issues, gps.previousIssues, gps.startDate, gps.endDate, gps.previousStartDate, gps.previousEndDate, excludeDrafts]);

  return stats;
};
